import React from 'react';
import {
  Action, ActionProps, Message, ModalSize, Portal, useModal,
} from '@doveit/bricks';
import SimpleModal from '../../../components/simple-modal/SimpleModal';
import { Agent, ManagerGoal } from '../../../providers/api/dtos';
import { ManagerGoalSimulationView, ManagerGoalSimulationViewProps } from '../manager-goal-simulation-view/ManagerGoalSimulationView';
import useManagerGoal from '../../hooks/use-manager-goal/useManagerGoal';

interface ChildFnProps {
  openEditModal: VoidFunction,
  isLoading: boolean,
}

export interface EditManagerGoalActionProps {
  agentId: NonNullable<Agent['id']>,
  label?: ActionProps['label'],
  size?: ActionProps['size'],
  children?: (childFnProps: ChildFnProps) => React.ReactNode,
  onGoalAccepted?: ManagerGoalSimulationViewProps['onGoalAccepted']
}

export const LOAD_GOAL_ERROR_MESSAGE = 'Non è stato possibile caricare le informazioni relative all\'obiettivo di fatturato.';

export const EditManagerGoalAction: React.FC<EditManagerGoalActionProps> = ({
  agentId,
  label = 'Modifica obiettivo',
  size = 'S',
  onGoalAccepted,
  children,
}) => {
  const {
    data: existingGoal,
    isLoading: isExistingGoalLoading,
    error: existingGoalError,
    mutate: mutateExistingGoal,
  } = useManagerGoal(agentId);
  const editGoalModal = useModal();

  const openEditModal = React.useCallback(() => {
    editGoalModal.open();
  }, [editGoalModal]);

  const onInternalGoalAccepted = React.useCallback((acceptedGoal: ManagerGoal) => {
    editGoalModal.close();
    mutateExistingGoal();
    onGoalAccepted?.(acceptedGoal);
  }, [editGoalModal, mutateExistingGoal, onGoalAccepted]);

  if (existingGoalError && existingGoalError.status === 404) {
    return null;
  }

  return (
    <>
      {
        children
          ? children({ isLoading: isExistingGoalLoading, openEditModal })
          : (
            <Action
              label={label}
              aria-label="Pulsante per modificare l'obiettivo di fatturato"
              loading={isExistingGoalLoading}
              onClick={openEditModal}
              size={size}
            />
          )
      }
      <Portal>
        <SimpleModal
          {...editGoalModal}
          title="Modifica l'obiettivo di fatturato"
          aria-label="Modifica obiettivo di fatturato"
          loading={isExistingGoalLoading}
          size={ModalSize.LARGE}
        >
          {(existingGoalError && existingGoalError.status !== 404) && (
            <Message
              message={LOAD_GOAL_ERROR_MESSAGE}
              type="critical"
            />
          )}
          {!existingGoalError && existingGoal && (
            <ManagerGoalSimulationView
              hideTutorial
              agentId={agentId}
              goal={existingGoal}
              onGoalAccepted={onInternalGoalAccepted}
            />
          )}
        </SimpleModal>
      </Portal>
    </>
  );
};

export default EditManagerGoalAction;
